import { APPOINTMENT_STATUSES, formatInZone, type AppointmentStatus } from "@so/shared";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMe, useQueue, type QueueItem } from "../api/queries.js";
import { SpecialtyBadge, StatusBadge, statusLabel } from "../components/appointment.js";
import { Badge, Card, EmptyState, ErrorNote, Loading, PageHeader, cx } from "../components/ui.js";

/**
 * Flags a coordinator should act on, worked out server-side and shown here.
 * Ordered by how much they matter, so the first badge on a row is the reason
 * it needs attention.
 */
function Flags({ item }: { readonly item: QueueItem }) {
  const { flags } = item;

  return (
    <div className="flex flex-wrap gap-1.5">
      {flags.in_the_past && <Badge tone="warn">Overdue</Badge>}
      {flags.starts_within_24h && <Badge tone="caution">Within 24 hours</Badge>}
      {flags.awkward_for_clinician && <Badge tone="caution">Unsociable for the specialist</Badge>}
      {flags.awkward_for_patient && <Badge tone="caution">Unsociable for the patient</Badge>}
      {flags.hours_apart >= 6 && (
        <Badge>
          {flags.hours_apart % 1 === 0 ? flags.hours_apart : flags.hours_apart.toFixed(1)} hours
          apart
        </Badge>
      )}
    </div>
  );
}

function QueueRow({ item, viewerTimezone }: { readonly item: QueueItem; readonly viewerTimezone: string }) {
  const local = formatInZone(item.starts_at, viewerTimezone);
  const urgent = item.flags.in_the_past;

  return (
    <Card
      as="li"
      className={cx("transition-colors hover:border-line-strong", urgent && "border-warn/40")}
    >
      <Link
        to={`/consultations/${item.appointment_id}`}
        className="flex flex-wrap items-start gap-x-5 gap-y-3 px-5 py-4"
      >
        <div className="min-w-[260px] flex-1">
          <p className="text-[15.5px] font-medium">
            {item.patient_name}
            <span className="text-ink-faint mx-2 font-normal">with</span>
            {item.clinician_name}
          </p>

          <p className="text-ink-soft mt-1 text-[14px]">
            {local.date}
            <span className="text-ink-faint mx-1.5">·</span>
            <span className="font-mono">{local.time}</span>
            <span className="text-ink-faint ml-1.5 font-mono text-[12.5px]">
              {local.zoneAbbreviation}
            </span>
          </p>

          <div className="mt-2">
            <Flags item={item} />
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={item.status} />
          <SpecialtyBadge specialty={item.specialty} />
        </div>
      </Link>
    </Card>
  );
}

export function Queue() {
  const { data: me } = useMe();
  const [status, setStatus] = useState<AppointmentStatus>("requested");
  const { data, isPending, error } = useQueue(status);

  const viewerTimezone = me?.timezone ?? "UTC";

  return (
    <>
      <PageHeader eyebrow="Coordination" title="Work queue">
        Every consultation in the chosen state, across all patients — overdue first, then whatever
        is closest. This is the only view that reaches across patients, and every record you open
        from here is logged against your name.
      </PageHeader>

      <div className="mb-6 flex flex-wrap gap-1.5">
        {APPOINTMENT_STATUSES.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setStatus(value)}
            aria-pressed={status === value}
            className={cx(
              "rounded-md border px-3 py-1.5 text-[14px] transition-colors",
              status === value
                ? "border-accent bg-accent text-white"
                : "border-line-strong bg-surface text-ink-soft hover:bg-surface-2",
            )}
          >
            {statusLabel(value)}
          </button>
        ))}
      </div>

      {isPending ? (
        <Loading label="Loading the queue" />
      ) : error ? (
        <ErrorNote error={error} />
      ) : data.appointments.length === 0 ? (
        <EmptyState title={`Nothing ${statusLabel(status).toLowerCase()}`}>
          When a consultation reaches this state it will appear here.
        </EmptyState>
      ) : (
        <>
          {data.needs_attention > 0 && (
            <p className="text-warn mb-4 text-[14.5px] font-medium">
              {data.needs_attention} of {data.count} need attention now.
            </p>
          )}

          <ul className="space-y-2.5">
            {data.appointments.map((item) => (
              <QueueRow key={item.appointment_id} item={item} viewerTimezone={viewerTimezone} />
            ))}
          </ul>
        </>
      )}
    </>
  );
}
